import { Box, Button, Container, Grid2, Skeleton, Stack } from "@mui/material";
import { HeaderView } from "../../component/HeaderView";
import { useTranslation } from "react-i18next";
import { Player } from "../../../domain/model/User";
import { LoadingView } from "../../component/LoadingView";
import { CustomCardView } from "../../component/CustomCardView";
import { CustomTextView } from "../../component/CustomTextView";
import { SparkLineChart } from "@mui/x-charts";
import {
  COLOR_BLACK,
  COLOR_LIGHT_GRAY,
  COLOR_WHITE,
} from "../../../data/colors/Colors";
import { CustomImageView } from "../../component/CustomImageView";
import useWindowDimensions from "../../../core/utils/UseWindowDimensions";
import { GamesRounded, HistoryRounded } from "@mui/icons-material";
import moment from "moment";
import "moment/locale/ar";
import { CustomPopoverView } from "../../component/CustomPopoverView";
import { ReactNode, useEffect, useState } from "react";
import { ChoosePackageView } from "../choosePackage/ChoosePackageView";
import { useNavigate } from "react-router-dom";
import { ROUTE_ENDPOINTS } from "../../../data/constant/RouteConstants";
import { CategoryItemView } from "../playedGames/PlayedGamesView";
import SharedPreferences from "../../../core/utils/SharedPreferences";
import { STORAGE_KEYS } from "../../../data/constant/Constants";
import { DashboardViewModel } from "./DashboardViewModel";
import {
  provideGetAllTransactionsUseCase,
  provideGetPlayerCategoriesStatsUseCase,
  provideGetPlayerCurrentGamesUseCase,
} from "../../di/UseCasesModule";

export function DashboardView({ player }: { player: Player | undefined }) {
  const { t, i18n } = useTranslation();

  const { width, height } = useWindowDimensions();
  const isMobile = width < 700 || height < 700;

  const navigate = useNavigate();
  const viewModel = DashboardViewModel(
    provideGetPlayerCategoriesStatsUseCase(),
    provideGetAllTransactionsUseCase(),
    provideGetPlayerCurrentGamesUseCase()
  );

  useEffect(() => {
    if (player?._id) viewModel.getCategoriesStats(player._id);
  }, [player]);

  const [anchorElPosition, setAnchorElPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  return (
    <Stack>
      <HeaderView title={t("dashboard")} />

      {viewModel.loading ? (
        <LoadingView />
      ) : (
        <>
          <Container maxWidth={false}>
            <Grid2 container spacing={2} sx={{ pb: 3, pt: 3 }}>
              <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 4 }}>
                <ContainerView
                  title={t("total_played_games")}
                  header={viewModel.games?.length.toString() ?? ""}
                  headerFontSize="h3"
                  height={isMobile ? "25vmax" : "25vh"}
                  layout={
                    <SparkLineChart
                      data={viewModel.calculateMonthlyData({
                        games: viewModel.games,
                      })}
                      height={viewModel.games?.length == 0 ? 0 : 150}
                      curve="catmullRom"
                      colors={[COLOR_LIGHT_GRAY]}
                    />
                  }
                />
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 4 }}>
                <ContainerView
                  title={t("most_picked_category")}
                  header={
                    viewModel.mostPickedCategory
                      ? viewModel.mostPickedCategory.title
                      : "_"
                  }
                  height={isMobile ? "25vmax" : "25vh"}
                  layoutWidthPercentage="45%"
                  isLoading={viewModel.isGettingCategoriesStats}
                  layout={
                    <>
                      {viewModel.isGettingCategoriesStats ? (
                        <Skeleton
                          width={"100%"}
                          height={80}
                          sx={{ borderRadius: 4 }}
                        />
                      ) : (
                        viewModel.mostPickedCategory && (
                          <CustomImageView
                            width={"100%"}
                            height={"100%"}
                            src={viewModel.mostPickedCategory?.imageUrl}
                            scale={"contain"}
                            props={{ pt: 2 }}
                          />
                        )
                      )}
                    </>
                  }
                />
              </Grid2>
              {false && (
                <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 4 }}>
                  <ContainerView
                    title={t("total_spent_money")}
                    header={`$${viewModel.totalSpentMoney.toFixed(2)}`}
                    headerFontSize="h3"
                    height={isMobile ? "25vmax" : "25vh"}
                    isLoading={viewModel.isGettingAllTransactions}
                    layout={
                      <>
                        {viewModel.isGettingAllTransactions ? (
                          <Skeleton
                            width={"100%"}
                            height={80}
                            sx={{ borderRadius: 4 }}
                          />
                        ) : (
                          <SparkLineChart
                            data={viewModel.transactionsDataSet}
                            height={viewModel.totalSpentMoney == 0 ? 0 : 150}
                            colors={[COLOR_LIGHT_GRAY]}
                            curve="step"
                          />
                        )}
                      </>
                    }
                  />
                </Grid2>
              )}

              <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 4 }}>
                <ContainerView
                  title={t("games_left_to_play_")}
                  header={player?.gamesLeftToPlay?.toString() ?? ""}
                  headerFontSize="h3"
                  height={isMobile ? "25vmax" : "25vh"}
                  layoutWidthPercentage={isMobile ? "80%" : undefined}
                  layout={
                    <Stack width={"100%"} gap={1}>
                      <Button
                        sx={{
                          borderRadius: 12,
                          background: COLOR_BLACK,
                          border: "solid 1px " + COLOR_BLACK,
                          textTransform: "none",
                        }}
                        onClick={(e) => {
                          if (
                            SharedPreferences.getBoolean(
                              STORAGE_KEYS.LOGGED_IN_AS_GUEST
                            ) === true
                          ) {
                            alert("You need to signup");
                            return;
                          }
                          setAnchorElPosition({
                            top: e.clientY,
                            left: e.clientX,
                          });
                        }}
                      >
                        <Stack direction={"row"} alignItems={"center"} gap={1}>
                          <CustomTextView
                            text={t("buy_more_games")}
                            textColor={COLOR_WHITE}
                            fontWeight="bold"
                            textAlign="center"
                          />
                          <GamesRounded sx={{ color: COLOR_WHITE }} />
                        </Stack>
                      </Button>
                      <Button
                        sx={{
                          borderRadius: 12,
                          border: "solid 1px " + COLOR_BLACK,
                          textTransform: "none",
                        }}
                        onClick={() => {
                          window.scrollTo(0, 0);
                          navigate(ROUTE_ENDPOINTS.HOME_PLAYED_GAMES);
                        }}
                      >
                        <Stack direction={"row"} alignItems={"center"} gap={1}>
                          <CustomTextView
                            text={t("see_games_history")}
                            textColor={COLOR_BLACK}
                            textAlign="center"
                          />
                          <HistoryRounded sx={{ color: COLOR_BLACK }} />
                        </Stack>
                      </Button>
                    </Stack>
                  }
                />
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <ContainerView
                  title={t("previously_played_game")}
                  header={
                    viewModel.games?.length != 0
                      ? moment(viewModel.games![0]?.date)
                          .locale(i18n.resolvedLanguage ?? "ar")
                          .fromNow()
                      : "_"
                  }
                  headerFontSize="h4"
                  minHeight="30vh"
                  layoutWidthPercentage={isMobile ? "100%" : "60%"}
                  isColumnInMobile={width < 1500}
                  layout={
                    viewModel.games?.length != 0 ? (
                      <Grid2 container spacing={1}>
                        {viewModel.games![0]?.selectedCategories.map((sc) => {
                          const categoryEntity = sc.category;
                          return (
                            <Grid2 size={4} key={categoryEntity._id}>
                              <CategoryItemView
                                category={categoryEntity}
                                height={isMobile ? 120 : 180}
                                textVariant="h6"
                                textBgColor={COLOR_BLACK}
                              />
                            </Grid2>
                          );
                        })}
                      </Grid2>
                    ) : (
                      <></>
                    )
                  }
                />
              </Grid2>
            </Grid2>
          </Container>
        </>
      )}

      <CustomPopoverView
        onClose={() => {
          setAnchorElPosition(null);
        }}
        anchorElPosition={anchorElPosition}
        width={isMobile ? "100vw" : "30vw"}
        height="100vh"
        layout={
          <>
            {player && (
              <ChoosePackageView
                player={player}
                handleClose={() => setAnchorElPosition(null)}
              />
            )}
          </>
        }
      />
    </Stack>
  );
}

function ContainerView({
  title,
  header,
  headerFontSize = "h4",
  layout,
  height,
  minHeight,
  layoutWidthPercentage = "50%",
  isColumnInMobile = false,
  isLoading,
}: {
  title: string;
  header: string;
  headerFontSize?: "h4" | "h3";
  layout: ReactNode;
  layoutWidthPercentage?: string;
  height?: string;
  minHeight?: string;
  isColumnInMobile?: boolean;
  isLoading?: boolean;
}) {
  return (
    <CustomCardView
      width={"100%"}
      height={height}
      minHeight={minHeight}
      children={
        <Stack
          direction={isColumnInMobile ? "column" : "row"}
          width={"100%"}
          height={"100%"}
          justifyContent={"space-between"}
          gap={isColumnInMobile ? 4 : 1}
        >
          <Stack height={"100%"}>
            <CustomTextView text={title} variant="h6" />

            {isLoading ? (
              <Skeleton height={40} sx={{ borderRadius: 2 }} />
            ) : (
              <CustomTextView
                text={header}
                variant={headerFontSize}
                fontWeight="bold"
              />
            )}
          </Stack>
          <Stack
            width={isColumnInMobile ? "100%" : layoutWidthPercentage}
            alignItems={"end"}
            height={"100%"}
            justifyContent={"end"}
            gap={1}
          >
            {layout}
          </Stack>
        </Stack>
      }
    />
  );
}
